import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../../../../scss/components/_ag-table.scss";
import { getServerSideDatasourcePPC, getPubAbbr } from "@revbox/helpers-fe";
import "ag-grid-charts-enterprise";
import {
  PositionRenderer,
  PositionCellTooltipRenderer,
  PositionColumnHeaderTooltipRenderer,
} from "./EmailPositionRenderer";
import { StatusCellTooltipRenderer, StatusColumnHeaderTooltipRenderer, StatusRenderer } from "./EmailStatusRenderer";
import { ActionCellColumnHeaderTooltipRenderer, ActionCellTooltipRenderer, ActionRenderer } from "./EmailActionRenderer";
import {
  MissedClicksForecastCellColumnHeaderTooltipRenderer,
  MissedClicksForecastCellTooltipRenderer,
  MissedClicksForecastRenderer,
} from "./EmailMissedClicksForecastRenderer";
import { NameRenderer, NameCellTooltipRenderer, NameColumnHeaderTooltipRenderer } from "./EmailNameRenderer";
import {
  YourBidCellColumnHeaderTooltipRenderer,
  YourBidCellRenderer,
  YourBidCellTooltipRenderer,
} from "./EmailYourBidRenderer";
import { TopBidCellColumnHeaderTooltipRenderer, TopBidCellRenderer, TopBidCellTooltipRenderer } from "./EmailTopBidRenderer";
import { YourClicksCellColumnHeaderTooltipRenderer, YourClicksCellTooltipRenderer, ClicksCellRenderer } from "./EmailClicksRenderer";
import { CustomTooltip } from "../../common/CustomTooltip";
import { Card } from "react-bootstrap";
import { TaxonomyModule } from "../../../../../modules/taxonomy";
import { emailsPPCAuctionsLabels, searchPlaceholder } from "@revbox/localization";
import { useCookiesContext } from "../../../../../contexts/CookiesContext";


const customColumns = [
  {
    field: "Name",
    cellRenderer: NameRenderer,
    headerTooltip: NameColumnHeaderTooltipRenderer(),
    tooltipValueGetter: NameCellTooltipRenderer
  },
  {
    field: "Position",
    cellRenderer: PositionRenderer,
    headerTooltip: PositionColumnHeaderTooltipRenderer(),
    tooltipValueGetter: PositionCellTooltipRenderer,
  },
  {
    field: "Status",
    cellRenderer: StatusRenderer,
    headerTooltip: StatusColumnHeaderTooltipRenderer(),
    tooltipValueGetter: StatusCellTooltipRenderer,
  },
  {
    field: "Your Bid",
    cellRenderer: YourBidCellRenderer,
    headerTooltip: YourBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourBidCellTooltipRenderer
  },
  {
    field: "Top Bid",
    cellRenderer: TopBidCellRenderer,
    headerTooltip: TopBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: TopBidCellTooltipRenderer
  },
  {
    field: "Your Clicks",
    cellRenderer: ClicksCellRenderer,
    headerTooltip: YourClicksCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourClicksCellTooltipRenderer,
  },
  {
    field: "Action",
    cellRenderer: ActionRenderer,
    headerTooltip: ActionCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: ActionCellTooltipRenderer
  },
  {
    field: "Missed Clicks Forecast",
    cellRenderer: MissedClicksForecastRenderer,
    headerTooltip: MissedClicksForecastCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: MissedClicksForecastCellTooltipRenderer,
  },
];

const defaultColDef = {
  flex: 1,
  minWidth: 90,
  tooltipComponent: CustomTooltip,
}

export const EmailsPPCAuctions = ({
  getData,
  skin,
  onClick,
  campaignId,
  updatePage,
  isHTR,
  display,
  setAnyActiveEmailsBid,
  setAnyEmailsAuctions,
  setEmailsActiveAuctions
}) => {
  const { getLanguageAbbr } = useCookiesContext();
  const languageAbbr = getLanguageAbbr();
  const multilang = emailsPPCAuctionsLabels[languageAbbr];
  const search_placeholder = searchPlaceholder[languageAbbr];
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ minHeight: "100%", width: "100%" }), []);

  const [search, setSearch] = useState("");
  const [gridApi, setGridApi] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  const [data, setData] = useState([]);

  const publisherIdByAbbr = getPubAbbr();
  const taxonomy = TaxonomyModule(publisherIdByAbbr);

  useEffect(() => {
    getData({search}, campaignId ).then((response) => {
      if (response.success) {
        const anyActiveBidFlag = response.data.rows.some(row => {
          const status = row[2].split("#")[0];
          const cases = ["Live", "Paused", "Outbid"]
          return cases.includes(status)
        });

        setAnyEmailsAuctions(response.data.rows.length > 0);
        setAnyActiveEmailsBid(anyActiveBidFlag);
        setEmailsActiveAuctions(response.data.rows);
        setData(response.data.rows);
      }
    });
    // updatePage is used to refresh the tabs when the page is updated
  }, [campaignId, updatePage]);


  const onFilterTextBoxChanged = (e) => {
    setSearch(e.target.value);
  };

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);

    const datasource = getServerSideDatasourcePPC(
      getData,
      setColumnDefs,
      search,
      customColumns,
      campaignId,
      languageAbbr
    );

    setTimeout(() => {
      params.api.setGridOption("serverSideDatasource", datasource);
    }, 2000);
  }, [campaignId]);

  useEffect(() => {
    if (gridApi) {
      const datasource = getServerSideDatasourcePPC(
        getData,
        setColumnDefs,
        search,
        customColumns,
        campaignId,
        languageAbbr
      );

      setTimeout(() => {
        gridApi.setGridOption("serverSideDatasource", datasource);
      }, 2000);
    }
  }, [gridApi, search, updatePage, campaignId]);

  if (display !== "emails" || data?.length === 0) {
    return <></>;
  }
  
  return (
    <Card className="card-one">
      <Card.Header className="d-flex flex-column align-items-start">
        <Card.Title as="h6" className="mb-2">
          {isHTR && multilang.htrTitle}
          {!isHTR && multilang.title}
        </Card.Title>
        { isHTR &&
          <Card.Subtitle as="p">
            {multilang.htrSubtitle}
            <Card.Link
              target="_blank"
              className="ps-2"
              href="https://help.hoteltechreport.com/en/collections/10828721-auction-types"
            >
              {multilang.learnMore}
            </Card.Link>
          </Card.Subtitle>
        }
      </Card.Header>
      <Card.Body>
        <div style={containerStyle}>
          <div
            style={gridStyle}
            className={skin === "dark" ? "ag-theme-quartz-dark ag-theme-quartz" : "ag-theme-quartz-light ag-theme-quartz"}
          >
            <div style={{ marginBottom: "10px" }}>
              <input
                type="text"
                className="input-search"
                placeholder={search_placeholder}
                onInput={onFilterTextBoxChanged}
              />
            </div>
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              tooltipShowDelay={500}
              tooltipHideDelay={200000}
              paginationPageSize={10}
              paginationPageSizeSelector={false}
              cacheBlockSize={10}
              rowModelType={"serverSide"}
              domLayout={"autoHeight"}
              suppressContextMenu={true}
              onCellClicked={onClick}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
